import { useParams } from 'react-router-dom';
import { CATEGORY_LABELS } from '@/constants/categories';
import { isEventActive } from '@/lib/helpers';
import { EventPublicCard } from '../components/EventPublicCard';
import { SeoHead } from '../components/SeoHead';
import { useCategoryEvents } from '../hooks/useCategoryEvents';
import { sortLongEventsLast } from '@/utils/longEvents';

// Copy única por categoria (H1 + parágrafo), pensada pra SEO local — cada
// frase menciona Joinville e soa natural, em vez de um template genérico
// "Eventos de {categoria} em Joinville" repetido em todas as páginas.
const CATEGORY_SEO = {
  musica: {
    h1: 'Shows e Eventos de Música em Joinville',
    description: 'Descubra os melhores shows, festivais e apresentações musicais em Joinville: dos palcos das principais casas de show a eventos independentes, tudo em um só lugar.',
  },
  teatro: {
    h1: 'Peças e Eventos de Teatro em Joinville',
    description: 'Confira a agenda de teatro em Joinville: peças, espetáculos e apresentações para todos os gostos e idades.',
  },
  esportes: {
    h1: 'Eventos Esportivos em Joinville',
    description: 'Corridas, campeonatos e eventos esportivos acontecendo em Joinville — encontre a próxima competição pra participar ou torcer.',
  },
  palestras: {
    h1: 'Palestras e Eventos de Conhecimento em Joinville',
    description: 'Palestras, workshops e eventos voltados a aprendizado e networking em Joinville.',
  },
  festival: {
    h1: 'Festivais em Joinville',
    description: 'Os melhores festivais de música, gastronomia e cultura acontecendo em Joinville, em um só lugar.',
  },
  encontros: {
    h1: 'Encontros e Eventos Sociais em Joinville',
    description: 'Encontros, meetups e eventos sociais em Joinville pra você conhecer gente nova e se conectar com sua comunidade.',
  },
  gastronomia: {
    h1: 'Eventos Gastronômicos em Joinville',
    description: 'Feiras, festivais gastronômicos e experiências culinárias em Joinville — pra quem ama boa comida e bebida.',
  },
  infantil: {
    h1: 'Eventos Infantis em Joinville',
    description: 'Eventos, oficinas e atrações pensadas pra criançada se divertir em Joinville.',
  },
  cultura: {
    h1: 'Eventos Culturais em Joinville',
    description: 'Exposições, mostras e eventos culturais em Joinville pra quem quer viver a cena cultural da cidade.',
  },
  outros: {
    h1: 'Outros Eventos em Joinville',
    description: 'Confira outros eventos acontecendo em Joinville que não se encaixam nas categorias tradicionais.',
  },
};

// Mesmo skeleton visual usado na Home (HomePage.jsx) enquanto os eventos
// carregam, pra manter a mesma linguagem de loading em todo o site.
function CategorySkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="rounded-2xl overflow-hidden border border-border/60 bg-card animate-pulse">
          <div className="aspect-video bg-muted" />
          <div className="p-4 space-y-3">
            <div className="h-4 bg-muted rounded w-3/4" />
            <div className="h-3 bg-muted rounded w-1/2" />
            <div className="h-3 bg-muted rounded w-2/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function CategoryPage() {
  const { slug } = useParams();
  const { events: categoryEvents, loading, error } = useCategoryEvents(slug);
  // Só eventos ainda não encerrados (mesma checagem da Home, em
  // publicEventService.getHomeEvents). Categoria é um filtro: eventos
  // longos sempre aparecem, mas no fim da lista (impulsionados ficam onde
  // estão) — ver utils/longEvents.js.
  const events = sortLongEventsLast(categoryEvents.filter((e) => isEventActive(e._raw)));

  const label = CATEGORY_LABELS[slug] || slug;
  const seo = CATEGORY_SEO[slug] || {
    h1: `Eventos de ${label} em Joinville`,
    description: `Descubra os melhores eventos de ${label.toLowerCase()} em Joinville, tudo em um só lugar.`,
  };

  return (
    <div className="max-w-[1920px] mx-auto px-4 md:px-8 py-12 md:py-16">
      <SeoHead title={`${label} em Joinville — Joyn`} description={seo.description} />

      <div className="max-w-2xl mb-10">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{seo.h1}</h1>
        <p className="text-muted-foreground mt-3 text-base leading-relaxed">{seo.description}</p>
      </div>

      {error && <p className="text-sm text-destructive mb-6">{error}</p>}

      {loading ? (
        <CategorySkeleton />
      ) : events.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6">
          Nenhum evento de {label.toLowerCase()} no momento. Volte em breve!
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {events.map((event) => (
            <EventPublicCard key={event.id} event={event} />
          ))}
        </div>
      )}
    </div>
  );
}
